<template>
  <div
    class="heading-copy"
    :class="content.textColour ? `text-colour--${content.textColour}` : 'text-colour--dark'"
  >
    <div class="heading-copy__content">
      <header
        v-if="content.subheadingText || content.headingText"
      >
        <p
          v-if="content.subheadingText"
          class="heading-copy__subheading text-other-label-s text-other-label-l-desktop"
          :class="classes.subheading"
          v-text="content.subheadingText"
        />

        <component
          :is="headingElement"
          v-if="content.headingText"
          :id="content.headingId ? content.headingId : null"
        >
          <component
            :is="content.headingLink ? 'a' : 'span'"
            class="heading-copy__title"
            :class="[
              classes.heading,
              content.headingSizeDesktop,
              content.headingSizeMobile,
              { 'stretched-link': content.isHeadingStretchedLink }
            ]"
            :href="content.headingLink"
          >
            <span v-text="content.headingText" />
          </component>
        </component>
      </header>

      <div
        v-if="content.subText"
        class="heading-copy__text"
        v-html="getBodyText(content.subText)"
      />

      <div
        v-if="showButtonContainer"
        class="heading-copy__button-container"
      >
        <btn
          class="heading-copy__button"
          :label="content.buttonText"
          :modifiers="`${content.buttonStyle} ${content.buttonMode}`"
          :url="content.buttonUrl"
        />
        <btn
          v-if="content.secondButtonText && content.secondButtonUrl"
          class="heading-copy__button"
          :label="content.secondButtonText"
          :modifiers="`${content.secondButtonStyle} ${content.secondButtonMode}`"
          :url="content.secondButtonUrl"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Heading copy (heading-copy)
 * -----------------------------------------------------------------------------
 * Heading copy.
 *
 * @param {Object} [classes] - Object containing text classes.
 * @param {String} [classes.copy] - Text classes for copy/subtext.
 * @param {String} [classes.heading] - Text classes for heading.
 * @param {String} [classes.subheading] - Text classes for subheading.
 * @param {Object} content - Heading content.
 * @param {String} [content.headingElement] - Heading element.
 * @param {String} [content.subheadingText] - Subheading above heading.
 * @param {String} [content.headingId] - Unique ID for heading element.
 * @param {String} [content.headingLink] - URL attached to heading.
 * @param {String} [content.headingText] - Heading text.
 * @param {String} [content.subText] - Text below heading.
 * @param {String} [content.buttonText] - CTA label.
 * @param {String} [content.buttonUrl] - CTA text.
 * @param {String} [content.buttonStyle] - CTA style.
 * @param {String} [content.buttonMode] - CTA mode.
 * @param {String} [content.secondButtonText] - Second CTA label.
 * @param {String} [content.secondButtonUrl] - Second CTA text.
 * @param {String} [content.secondButtonStyle] - Second CTA style.
 * @param {String} [content.secondButtonMode] - Second CTA mode.
 * @param {String} [content.textColour] - Text colour.
 *
 */
import Btn from '~global/btn/btn'

export default {
  name: 'HeadingCopy',

  components: {
    Btn,
  },

  props: {
    classes: {
      type: Object,
      default: () => ({
        copy: '',
        heading: '',
        subheading: '',
      }),
    },
    content: {
      type: Object,
      default: () => ({
        headingElement: 'h2',
        subheadingText: '',
        headingId: '',
        headingLink: '',
        headingText: '',
        subText: '',
        buttonText: '',
        buttonUrl: '',
        buttonStyle: '',
        buttonMode: '',
        secondButtonText: '',
        secondButtonUrl: '',
        secondButtonStyle: '',
        secondButtonMode: '',
        textColour: '',
      }),
      required: true,
    },
  },

  computed: {

    /**
     * Compute heading element.
     * @returns {String}
     */
    headingElement() {
      return this.content.headingElement
        ? this.content.headingElement
        : 'h2'
    },

    /**
     * Compute buttons.
     * @returns {Boolean}
     */
    showButtonContainer() {
      return (this.content.buttonText && this.content.buttonUrl) ||
        (this.content.secondButtonText && this.content.secondButtonUrl)
    },
  },

  methods: {

    /**
     * Get body text.
     * - Applies text class to elements.
     * @param {String} text - Body copy text.
     * @returns {String}
     */
    getBodyText(text) {
      return text
        .replaceAll('<p></p>', '')
        .replaceAll('<a ', '<a class="text-body-s-bold text-body-m-bold-desktop"')
        .replaceAll('<ul', '<ul class="text-body-s-bold text-body-m-bold-desktop"')
        .replaceAll('<p', `<p class="${this.classes.copy}"`)
        .replaceAll('<strong', '<strong class="text-body-s-bold text-body-m-bold-desktop"')
    },
  },

}
</script>

<style lang="scss">
@import './heading-copy';
</style>
